// LocationSelector.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

type FeatureType =
  | 'country'
  | 'region'
  | 'place'
  | 'district'
  | 'locality'
  | 'postcode'
  | 'poi'
  | string;

type MapboxSuggestion = {
  name: string;
  mapbox_id: string;
  feature_type: FeatureType;
  place_formatted?: string;
  context?: {
    country?: {
      id: string;
      name: string;
      country_code?: string;
      country_code_alpha_3?: string;
    };
    region?: {
      id: string;
      name: string;
      region_code?: string;
      region_code_full?: string;
    };
    district?: { id: string; name: string };
    place?: { id: string; name: string };
    locality?: { id: string; name: string };
  };
  language?: string;
  maki?: string;
  metadata?: Record<string, any>;
};

export type LocationItem = {
  mapbox_id: string;
  feature_type: FeatureType;
  name: string;
  place_formatted?: string;
  session_token: string;
  country: {
    id: string;
    name: string;
    country_code?: string;
    country_code_alpha_3?: string;
  };
  subregion?: {
    id: string;
    name: string;
    region_code?: string;
    region_code_full?: string;
  };
  city?: {
    id?: string;
    name: string;
  };
};

export const LocationSelector: React.FC<{
  onChange: (locations: LocationItem[]) => void;
}> = ({ onChange }) => {
  const [searchLocation, setSearchLocation] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [resSearchLocation, setResSearchLocation] = useState<{
    suggestions?: MapboxSuggestion[];
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const sessionTokenRef = useRef<string>('');

  const newSessionToken = () => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const startNewSession = () => {
    sessionTokenRef.current = newSessionToken();
  };

  const ensureSession = () => {
    if (!sessionTokenRef.current) startNewSession();
  };

  const endSession = () => {
    sessionTokenRef.current = '';
  };

  // Click outside => close dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Fetch suggestions
  useEffect(() => {
    const q = searchLocation.trim();

    if (!q) {
      setResSearchLocation(null);
      setIsOpen(false);
      return;
    }

    ensureSession();

    let aborted = false;

    const run = async () => {
      try {
        const params: Record<string, string> = {
          q,
          access_token: process.env.REACT_APP_MAPBOX_PUBLIC_ACCESS_TOKEN || '',
          session_token: sessionTokenRef.current,
          language: 'en',
          country: process.env.REACT_APP_MAPBOX_ALLOWED_COUNTRIES || '',
          types: 'country,region,place',
        };

        const res = await fetch(
          `${process.env.REACT_APP_MAPBOX_SEARCH_API_URL}?${new URLSearchParams(
            params,
          )}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
          },
        );

        if (!res.ok) {
          const bodyText = await res.text().catch(() => '');
          console.error('Error fetching locations:', res.status, bodyText);
          return;
        }

        const json = (await res.json()) as { suggestions?: MapboxSuggestion[] };

        if (!aborted) {
          setResSearchLocation(json);
          setIsOpen(true);
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
      }
    };

    run();
    return () => {
      aborted = true;
    };
  }, [searchLocation]);

  // Notify parent on locations change
  useEffect(() => {
    onChange(locations);
  }, [locations, onChange]);

  const suggestions = useMemo(
    () => resSearchLocation?.suggestions || [],
    [resSearchLocation],
  );

  const alreadyAdded = (mapboxId: string) =>
    locations.some((x) => x.mapbox_id === mapboxId);

  const suggestionToLocationItem = (
    s: MapboxSuggestion,
    session_token: string,
  ): LocationItem | null => {
    const country = s?.context?.country;
    if (!country?.id || !country?.name) return null;

    const region = s?.context?.region;
    const featureType = s.feature_type;

    const cityName = featureType === 'place' ? s.name : undefined;

    return {
      mapbox_id: s.mapbox_id,
      feature_type: featureType,
      name: s.name,
      place_formatted: s.place_formatted,

      session_token,

      country: {
        id: country.id,
        name: country.name,
        country_code: country.country_code,
        country_code_alpha_3: country.country_code_alpha_3,
      },
      subregion: region
        ? {
            id: region.id,
            name: region.name,
            region_code: region.region_code,
            region_code_full: region.region_code_full,
          }
        : undefined,
      city: cityName ? { name: cityName } : undefined,
    };
  };

  const addSuggestion = (s: MapboxSuggestion) => {
    ensureSession();

    const tokenUsed = sessionTokenRef.current;

    const item = suggestionToLocationItem(s, tokenUsed);
    if (!item) return;

    if (alreadyAdded(item.mapbox_id)) {
      setIsOpen(false);
      return;
    }

    setLocations((prev) => [...prev, item]);

    endSession();

    setSearchLocation('');
    setResSearchLocation(null);
    setIsOpen(false);
  };

  const formatSecondaryLine = (l: MapboxSuggestion) => {
    const countryName = l?.context?.country?.name || '';
    return l?.place_formatted || countryName || '';
  };

  const typeBadge = (t?: string) => (t ? t.toUpperCase() : '');

  return (
    <div ref={containerRef} className="space-y-4">
      <div className="relative">
        <input
          type="text"
          value={searchLocation}
          placeholder="Select Location (Country/Region/City)"
          onFocus={() => {
            startNewSession();
            if (suggestions.length > 0) setIsOpen(true);
          }}
          onChange={(e) => {
            ensureSession();
            setSearchLocation(e.target.value);
          }}
          className="w-full border rounded px-3 py-2"
        />

        {isOpen && suggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white border rounded shadow">
            {suggestions.map((l) => {
              const countryCode = l?.context?.country?.country_code || '';
              const secondary = formatSecondaryLine(l);
              const type = typeBadge(l?.feature_type);
              const isDisabled = alreadyAdded(l.mapbox_id);

              return (
                <li
                  key={l.mapbox_id}
                  onClick={() => {
                    if (!isDisabled) addSuggestion(l);
                  }}
                  className={[
                    'px-4 py-3 flex items-start gap-3',
                    isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer hover:bg-gray-50',
                  ].join(' ')}
                  title={isDisabled ? 'Already added' : ''}
                >
                  <div className="mt-1 text-gray-400">📍</div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900 truncate">
                        {l.name}
                      </div>

                      {type ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          {type}
                        </span>
                      ) : null}

                      {countryCode ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                          {countryCode}
                        </span>
                      ) : null}

                      {isDisabled ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                          ADDED
                        </span>
                      ) : null}
                    </div>

                    {secondary ? (
                      <div className="text-sm text-gray-500 truncate">
                        {secondary}
                      </div>
                    ) : null}
                  </div>

                  <div className="text-gray-300">›</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Selected locations list */}
      {locations.length > 0 && (
        <ul className="space-y-1">
          {locations.map((loc, i) => {
            const type = typeBadge(loc.feature_type);

            const line =
              loc.feature_type === 'country'
                ? loc.country.name
                : loc.feature_type === 'region'
                  ? `${loc.subregion?.name || loc.name} / ${loc.country.name}`
                  : `${loc.city?.name || loc.name}${
                      loc.subregion?.name ? ` / ${loc.subregion.name}` : ''
                    } / ${loc.country.name}`;

            return (
              <li
                key={loc.mapbox_id || i}
                className="flex justify-between items-center bg-gray-100 px-3 py-2 rounded"
              >
                <span className="min-w-0 flex items-center gap-2">
                  {type ? (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-white text-gray-700 border">
                      {type}
                    </span>
                  ) : null}
                  <span className="truncate">{line}</span>
                </span>

                <button
                  onClick={() =>
                    setLocations((prev) => prev.filter((_, idx) => idx !== i))
                  }
                  className="text-red-500 ml-3"
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
